import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { AppShell } from "./components/AppShell";
import { ErrorBoundary } from "./components/ErrorBoundary";

import Index from "./pages/Index";
import ImportData from "./pages/ImportData";
import Analytics from "./pages/Analytics";
import Admin from "./pages/Admin";
import AdminLogin from "./pages/AdminLogin";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CookiePolicy from "./pages/CookiePolicy";
import DataDeletion from "./pages/DataDeletion";
import { FacebookCallback } from "./pages/FacebookCallback";
import ComposePage from "./pages/ComposePage";
import MarketplaceNewPage from "./pages/MarketplaceNewPage";
import EventNewPage from "./pages/EventNewPage";
import ModerationPage from "./pages/ModerationPage";
import Advertise from "./pages/Advertise";
import TagTeamsPage from "./pages/TagTeamsPage";
import GrantsPage from "./pages/GrantsPage";
import { RequireVerified, RequireGreenCheck, RequireModerator } from "./components/guards/RouteGuards";
import { isFeatureEnabled } from "@/config/featureFlags";
import { assertRuntime } from "@/config/runtime";
import { registerServiceWorker } from "@/utils/serviceWorker";
import { initSentry } from "@/config/sentry";
import './config/i18n';

// Initialize Sentry before app starts
initSentry();

// Validate runtime config on app startup
assertRuntime();

const queryClient = new QueryClient();

const App = () => {
  console.log("=== APP COMPONENT LOADED ===");
  console.log("Current URL:", window.location.href);
  
  useEffect(() => {
    // Register service worker on mount
    registerServiceWorker().catch(console.error);
  }, []);
  
  // Component to protect Analytics route
  const ProtectedAnalyticsRoute = () => {
    if (!isFeatureEnabled('ANALYTICS_PAGE')) {
      return <Navigate to="/" replace />;
    }
    return <Analytics />;
  };
  
  // Component to protect Admin route
  const ProtectedAdminRoute = () => {
    if (!isFeatureEnabled('ADMIN_PAGE')) {
      return <Navigate to="/" replace />;
    }
    return <Admin />;
  };
  
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        
        <Routes>
              {/* All pages render inside AppShell for consistent mobile viewport */}
              <Route element={<AppShell />}>
                <Route path="/" element={<Index />} />
                <Route path="/profile" element={<Index />} />
                <Route path="/profile/edit" element={<Index />} />
                <Route path="/import" element={<ImportData />} />
                <Route path="/analytics" element={<ProtectedAnalyticsRoute />} />
                <Route path="/admin" element={<ProtectedAdminRoute />} />
                <Route path="/admin-login" element={<AdminLogin />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/cookies" element={<CookiePolicy />} />
                <Route path="/data-deletion" element={<DataDeletion />} />
                <Route path="/auth/facebook/callback" element={<FacebookCallback />} />
                <Route path="/advertise" element={<Advertise />} />
                <Route path="/tag-teams" element={<TagTeamsPage />} />
                <Route path="/grants" element={<GrantsPage />} />
                
                {/* Protected Routes */}
                <Route 
                  path="/compose" 
                  element={
                    <RequireVerified>
                      <ComposePage />
                    </RequireVerified>
                  } 
                />
                <Route 
                  path="/marketplace/new" 
                  element={
                    <RequireGreenCheck>
                      <MarketplaceNewPage />
                    </RequireGreenCheck>
                  } 
                />
                <Route 
                  path="/events/new" 
                  element={
                    <RequireGreenCheck>
                      <EventNewPage />
                    </RequireGreenCheck>
                  } 
                />
                <Route 
                  path="/moderation" 
                  element={
                    <RequireModerator>
                      <ModerationPage />
                    </RequireModerator>
                  } 
                />
                
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
